//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import { LEGACY_TOAST_OPTIONS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { SEARCH_FILTERS } from '@search-filters/constants/searchFilters';
import { COLLECTION_SETTINGS_FILTERS } from '@search-filters/constants/collectionSettingsFilters';

(function () {
  const constants = angular.module('curalate.constants', ['curalate']);

  // Note that these String -> Hex mappings come from curalate-variables.less
  constants.constant('CHART_COLORS', {
    // Greyscale
    '@marshmallow': '#f7f7f7',
    '@mushroom': '#e3e3e3',
    '@oyster': '#7e7e7e',
    '@oreo': '#3e3e3e',
    '@gray6': '#9ba0a5',
    '@gray7': '#868B8F',

    // Accent Colors
    '@mint3': '#81e2d2',
    '@cyan3': '#97dbe9',
    '@indigo3': '#e8c3fd',

    // Misc
    '@guacamole': '#18ae8f',
    // This is called pumpkin for legacy reasons. It should map to our primary color
    '@pumpkin': theme.colors.aliases.primary,
  });

  constants.constant('KEY_CODES', {
    ENTER: 13,
    SHIFT: 16,
    ESC: 27,
  });

  // Create the bookmarklet function as a string, and minify it slightly.
  // Pinterest urls by reverse ns lookup: https://securitytrails.com/list/ns/ns1.pinterest.com
  // Disabling prefer-template so we can have a simpler ES5 bookmarklet.
  /* eslint-disable prefer-template */
  const bookmarkletFn = function (host) {
    const r =
      /pinterest\.(at|es|ch|de|ie|jp|nz|dk|ru|cl|ca|it|fr|th|vn|ph|in|pt|nl|com(\.(au|mx|ec|pe|uy))?|co\.(uk|kr|in))/;
    const h = window.location.hostname;
    window.CURALATE_BOOKMARKLET_HOST = host;
    if (r.test(h)) {
      window.open(
        host + '/bookmarklet-pinterest?url=' + encodeURI(window.location.href),
        '',
        'width=377,height=565',
      );
    } else {
      (document.body.appendChild.toString().indexOf('[native code]') !== -1
        ? document.body.appendChild
        : HTMLElement.prototype.appendChild
      ).call(document.body, document.createElement('script')).src =
        `${host}/js/bookmarklet.js?${new Date().getTime()}`;
    }
  }
    .toString()
    .replace(/(\s\s+)|\n/g, ' ');
  /* eslint-enable prefer-template */

  constants.constant(
    'BOOKMARKLET_SCRIPT',
    `javascript:(${bookmarkletFn})("${window.location.origin}");`,
  );

  constants.constant('CONTENT', {
    ASYNC_ACTIONS: {
      REFRESH: 'refresh',
      LOADING_CONTENT: 'loading-content',
    },
    CLIENT_FILTER: 'client',
    USER_FILTER: 'user',
    UNSCHEDULED_FILTER: 'unscheduledFilter',
    ORIENTATION: {
      HORIZONTAL: 'Horizontal',
      VERTICAL: 'Vertical',
      SQUARE: 'Square',
    },
    MEDIA_ITEM_TYPE: {
      IMAGE: 'Image',
      VIDEO: 'Video',
      PANORAMA: 'Panorama',
    },
    VIDEO_STATUS: {
      FAILED: 'Failed',
      PROCESSING: 'Processing',
      READY: 'Ready',
      UNKNOWN: 'Unknown',
    },
    SUPPORTED_LIBRARY_FILTERS: [
      'campaignTypes',
      'distribution',
      'isDistributed',
      'userIds',
      'contentGroup',
      'mediaItemTypes',
      'itemTypes',
      'orientation',
      'rightsStatuses',
      'hasProducts',
      'labels',
      'spamFiltering',
      'socialFilters',
      'dateRange',
      'visualScore',
      'semanticTags',
      'experimentTags',
      'semanticSearchDetections',
      'excludedContentSources',
      'platforms',
      'minHeight',
      'maxHeight',
      'minWidth',
      'maxWidth',
      'minAspectRatio',
      'maxAspectRatio',
      'excludeMatchedInstagramItemForInstagramStory',
      'excludeMatchedMisItemForInstagramPost',
      'excludeOriginalRepostedInstagramItem',
    ],
    DISTRIBUTION: 'distribution',
    IS_DISTRIBUTED: 'isDistributed',
  });

  constants.constant('TOASTS', LEGACY_TOAST_OPTIONS);

  constants.constant('SEARCH_FILTERS', SEARCH_FILTERS);

  constants.constant('COLLECTION_SETTINGS', {
    KEYS: {
      NAME: 'name',
      FILTERS: 'filters',
      SOURCES: 'sources',
      CREATED_AT: 'createdAt',
      UPDATED_AT: 'updatedAt',
      SORT: 'sort',
    },
    STATUS_KEYS: {
      // should match CollectionStatusType.ts
      UNMODERATED: 'Unmoderated',
      REJECTED: 'Rejected',
      APPROVED: 'Approved',
    },
    CONNECT_VENDOR_KEYS: {
      // should match ConnectVendorType.ts
      ALL: 'All',
    },
    SORT_KEYS: {
      // should match CollectionSortType.ts
      ADDED_TO_COLLECTION_TIMESTAMP: 'AddedToCollectionTimestamp',
      ADDED_TO_LIBRARY_TIMESTAMP: 'AddedToLibraryTimestamp',
      COMMENTS: 'comments',
      ENGAGEMENT: 'engagement',
      FANREEL_LAST_MODERATED_TIMESTAMP: 'FanreelLastModeratedTimestamp',
      FOLLOWERS: 'Followers',
      LIKES: 'likes',
      SIMILARITY_TO_APPROVED: 'similarityToApproved',
      SYNDICATION_TIMESTAMP: 'syndicationTimestamp',
      TIME: 'PostedTimestamp',
      VISUAL_SCORE: 'visualScore',
    },
    NOTIFICATIONS: {
      REFRESH: 'REFRESH-NOTIFICATIONS',
      UPDATE: 'UPDATE-NOTIFICATION',
      CREATE: 'CREATE-NOTIFICATION',
      DELETE: 'DELETE-NOTIFICATION',
      ERROR: {
        CREATE: 'CREATE-ERROR-NOTIFICATION',
        UPDATE: 'UPDATE-ERROR-NOTIFICATION',
      },
    },
    FILTERS: COLLECTION_SETTINGS_FILTERS,
  });

  constants.constant('USERS_ACTIONS', {
    REFRESH_USERS: 'refresh-users',
  });

  constants.constant('TRACKED_SOURCES_ACTIONS', {
    REFRESH_TRACKED_SOURCES: 'refresh-tracked-sources',
  });

  constants.constant('INSTAGRAM_ACCOUNTS_ACTIONS', {
    REFRESH_INSTAGRAM_ACCOUNTS: 'refresh-instagram-accounts',
  });

  constants.constant(
    'IG_DEPRECATION_LEARN_MORE_URL',
    'https://www.curalate.com/blog/instagram-api-changes/',
  );
  constants.constant(
    'IG_HASHTAG_DEPRECATION_LEARN_MORE_URL',
    'https://support.bazaarvoice.com/s/article/Tracking-Instagram-Hashtags',
  );

  constants.constant('FANREEL_SETTINGS_SECTIONS', {
    CONTENT_SOURCES: 'Content Sources',
    EXCLUDED_CONTENT: 'Excluded Content',
    BLOCKED_CONTENT: 'Blocked Content',
  });
})();
